// Copyright 2026 Hardfin, Inc.
import { MovementFilter, getMovements } from '@/movement/api/get-movements';
import { movementStatusSchema } from '@/movement/api/types';
import { searchTermSchema } from '@/utilities/search-term';
import { useSuspenseQuery } from '@tanstack/react-query';
import { z } from 'zod';

export const movementsQueryKey = ['movements'];

interface UseMovementsOptions {
  filter?: MovementFilter;
}

export function useMovements(options: UseMovementsOptions = {}) {
  const { data, ...query } = useSuspenseQuery({
    queryKey: [...movementsQueryKey, options.filter],
    queryFn: () => getMovements(options.filter),
    refetchOnWindowFocus: true,
    refetchInterval: 30_000,
  });

  const movements = data.movements;
  const resultCount = data.resultCount;
  return { movements, resultCount, ...query };
}

export const movementSearchListingSchema = z.object({
  query: searchTermSchema.catch(undefined),
  status: movementStatusSchema.optional(),
  page: z.number().default(1).catch(1),
  itemIds: z.array(z.string()).optional().catch(undefined),
  originIds: z.array(z.string()).optional().catch(undefined),
  destinationIds: z.array(z.string()).optional().catch(undefined),
  hasShipment: z
    .array(z.enum(['yes', 'no']))
    .optional()
    .catch(undefined),
});

export type MovementSearchListing = z.infer<typeof movementSearchListingSchema>;
